import { countryLanguage } from 'components/Common/localizedText'

// The club's text blocks (ADR 0013), its About and its Directions: each written once in its original language, which the API
// translates into the other variants. A save carries each in the club's old field (`field`), which mirrors its original; the form holds
// the text there, and the language the author declared beside it.
export const CLUB_TEXT_BLOCKS = {
  about: { field: 'description', title: 'About the club', fields: [['body', 'About the club', 'textarea', 10000]] },
  directions: { field: 'directionsInstructions', title: 'Directions', fields: [['body', 'Directions', 'textarea', 5000]] },
}
const languageOf = name => CLUB_TEXT_BLOCKS[name].field + 'Language'

// The text of a block's original, as a read gives it.
export const originalBody = block => block?.variants?.[block.originalLanguage]?.content?.body || ''

// The form values of the blocks before their author writes: no text, and a language that follows the club's country.
export const initialClubTextValues = () => Object.fromEntries(Object.entries(CLUB_TEXT_BLOCKS)
  .flatMap(([name, { field }]) => [[field, ''], [languageOf(name), '']]))
// The form values a read of a saved club gives its blocks (`localizedTexts`): each original as its author wrote it, in its language. A
// read without blocks leaves them to the old fields, and a block without text, which the API derives for a club that never had one,
// leaves its language to follow the club's country.
export const clubTextFormValues = localizedTexts => Object.fromEntries(Object.entries(CLUB_TEXT_BLOCKS).flatMap(([name, { field }]) => {
  const block = localizedTexts?.[name]
  if (!block) return []
  const body = originalBody(block)
  return [[field, body], ...(body.trim() ? [[languageOf(name), block.originalLanguage]] : [])]
}))

// The original of the block `name` as the form holds it: in the language its author chose or wrote in, otherwise the country's.
export const clubTextOriginal = (values, name) => ({
  language: values[languageOf(name)] || countryLanguage(values.country),
  content: { body: values[CLUB_TEXT_BLOCKS[name].field] },
})
// The form values once the author declares the block's language.
export const clubTextDeclared = (name, language) => ({ [languageOf(name)]: language })
// The form values once the author writes the block's text, in the language it is shown in, which a later country no longer changes.
export const clubTextWritten = (values, name, content) => ({ ...clubTextDeclared(name, clubTextOriginal(values, name).language),
  [CLUB_TEXT_BLOCKS[name].field]: content.body })

// What a save sends of the block `name` in its old field: its original, with its language.
export const clubTextPayload = (values, name) => {
  const { language, content } = clubTextOriginal(values, name)
  return { originalLanguage: language, [language]: content.body.trim() }
}
// The blocks a new club's creation sends: those its author wrote.
export const createdClubTexts = values => Object.fromEntries(Object.entries(CLUB_TEXT_BLOCKS)
  .flatMap(([name, { field }]) => values[field].trim() ? [[field, clubTextPayload(values, name)]] : []))
